import { create } from 'zustand';

export type ThemePreference = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'once.theme';

function readStored(): ThemePreference {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (value === 'light' || value === 'dark' || value === 'system') return value;
  } catch {
    /* storage blocked (private mode, embedded webview) - fall back to system */
  }
  return 'system';
}

function systemPrefersDark(): boolean {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? true;
}

export function resolveTheme(preference: ThemePreference): 'light' | 'dark' {
  if (preference === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return preference;
}

/**
 * Applies the preference to <html>. `system` removes the attribute entirely so
 * the CSS media query takes over - that keeps one source of truth in CSS.
 */
function apply(preference: ThemePreference): void {
  const root = document.documentElement;
  if (preference === 'system') {
    root.removeAttribute('data-theme');
  } else {
    root.setAttribute('data-theme', preference);
  }

  // Keep the OS chrome (status bar, address bar) in step with the canvas.
  const resolved = resolveTheme(preference);
  const meta = document.querySelector('meta[name="theme-color"]');
  meta?.setAttribute('content', resolved === 'dark' ? '#0a0a0a' : '#f4f2ed');
}

interface ThemeState {
  preference: ThemePreference;
  setPreference: (preference: ThemePreference) => void;
  /** Cycles light → dark → system, used by the header toggle. */
  cycle: () => void;
  init: () => () => void;
}

export const useTheme = create<ThemeState>((set, get) => ({
  preference: readStored(),

  setPreference: (preference) => {
    try {
      localStorage.setItem(STORAGE_KEY, preference);
    } catch {
      /* non-fatal: the choice just will not survive a reload */
    }
    apply(preference);
    set({ preference });
  },

  cycle: () => {
    const order: ThemePreference[] = ['light', 'dark', 'system'];
    const next = order[(order.indexOf(get().preference) + 1) % order.length] ?? 'system';
    get().setPreference(next);
  },

  init: () => {
    apply(get().preference);
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      // Only matters while following the OS.
      if (get().preference === 'system') apply('system');
    };
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  },
}));
